import { Box, CircularProgress, Container, Divider, Typography, useMediaQuery } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useState, useLayoutEffect } from "react";
import SEO from "../Common/SEO";
import {
  veg_appetizers,
  non_veg_appetizers,
  bagaras,
  biryanis,
  speciality_rice,
  breafast_items,
  desserts,
  drinks,
} from "./menu-items";
import { MenuItem } from "./types";

const OTTER_URL =
  "https://order.tryotter.com/s/bagara's/594-east-el-camino-real-sunnyvale/e7a016c9-c780-4075-8cca-58272d6e7887";

const menuSections: { name: string; items: MenuItem[] }[] = [
  { name: "Veg Appetizers", items: veg_appetizers },
  { name: "Non-Veg Appetizers", items: non_veg_appetizers },
  { name: "Bagaras", items: bagaras },
  { name: "Biryanis", items: biryanis },
  { name: "Speciality Rice", items: speciality_rice },
  { name: "Breakfast Items", items: breafast_items },
  { name: "Desserts", items: desserts },
  { name: "Drinks", items: drinks },
];

const buildMenuJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Menu",
  "name": "Bagara's Menu",
  "description":
    "Full menu for Bagara's Indian restaurant in Sunnyvale — Hyderabadi biryani, bagara rice, Telugu curries, appetizers, desserts & drinks.",
  "url": "https://bagaras.com/menu",
  "hasMenuSection": menuSections.map((section) => ({
    "@type": "MenuSection",
    "name": section.name,
    "hasMenuItem": section.items
      .filter((item) => item.isAvailable)
      .map((item) => ({
        "@type": "MenuItem",
        "name": item.name,
        "offers": {
          "@type": "Offer",
          "price": item.price.toFixed(2),
          "priceCurrency": "USD",
        },
      })),
  })),
});

const MENU_TITLE = "Bagara's Menu & Order Online - Hyderabadi Biryani, Telugu Curries | Sunnyvale & Bay Area";

const MenuOrder = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const isDesktop = useMediaQuery("(min-width:600px)");
  const headerHeight = isDesktop ? 80 : 140;

  // Set title synchronously so react-snap captures it before Helmet's RAF fires
  useLayoutEffect(() => {
    document.title = MENU_TITLE;
  }, []);

  return (
    <div style={{
      margin: 0,
      padding: 0,
      paddingTop: `${headerHeight}px`,
      height: "100dvh",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <SEO
        title="Bagara's Menu & Order Online - Hyderabadi Biryani, Telugu Curries | Sunnyvale & Bay Area"
        description="Browse Bagara's full menu and order online. Hyderabadi biryani, bagara rice, gobi 65, chicken 555 & Telugu curries. Delivery & takeout in Sunnyvale."
        canonical="https://bagaras.com/menu"
        keywords="Bagara's menu, Indian food menu Sunnyvale, order biryani online, Hyderabadi biryani menu, Indian restaurant menu Bay Area, best biryani Sunnyvale, Indian food delivery, bagara rice, gobi 65, chicken 555, mutton biryani, Telugu food menu, Indian takeout menu"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(buildMenuJsonLd())}
        </script>
      </Helmet>

      <Box sx={{ width: "100%", m: 0, p: 0, position: "relative", flex: 1, minHeight: 0 }}>
        {!iframeLoaded && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              zIndex: 1,
            }}
          >
            <CircularProgress size={48} sx={{ color: "#20141e" }} />
          </Box>
        )}
        <iframe
          src={OTTER_URL}
          title="Order Bagara's food online"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            border: "none",
            margin: 0,
            padding: 0,
          }}
          loading="lazy"
          allow="payment"
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => setIframeLoaded(true)}
        />
      </Box>

      {/* Crawlable menu content for SEO — visually hidden but indexable by search engines */}
      <Container
        sx={{
          py: 4,
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          borderWidth: 0,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
          Bagara&#39;s Menu &amp; Online Ordering
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Authentic Hyderabadi biryani, bagara rice, Telugu curries, appetizers, desserts &amp; drinks.
          Order online for delivery or takeout in Sunnyvale and the Bay Area.
        </Typography>

        {menuSections.map((section) => (
          <Box key={section.name} sx={{ mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mb: 1 }}>
              {section.name}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {section.items
              .filter((item) => item.isAvailable)
              .map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.5,
                  }}
                >
                  <Typography variant="body1">{item.name}</Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    ${item.price.toFixed(2)}
                  </Typography>
                </Box>
              ))}
          </Box>
        ))}
      </Container>
    </div>
  );
};

export default MenuOrder;
